import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import marked from 'marked';
import sanitizeHTML from 'sanitize-html';

export {useDynamoPaginator} from "boss-ui/src/libs/hooks-lib"

const MD_DEFAULT_OPTIONS = {
  skipSanitize: false,
  markedOptions: null,
  sanitizeOptions: {
    allowedTags: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'p',
      'a',
      'ul',
      'ol',
      'nl',
      'li',
      'b',
      'i',
      'strong',
      'em',
      'strike',
      'code',
      'hr',
      'br',
      'div',
      'table',
      'thead',
      'caption',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'iframe',
    ],
    disallowedTagsMode: 'discard',
    allowedAttributes: {
      a: ['href', 'name', 'target'],
      img: ['src'],
    },
    selfClosing: ['img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta'],
    allowedSchemes: ['http', 'https', 'ftp', 'mailto'],
    allowedSchemesByTag: {},
    allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'],
    allowProtocolRelative: true,
  },
};

export function useFormFields(initialState) {
  const [fields, setValues] = useState(initialState);
  return [
    fields,
    function (event) {
      const value = event.target.value;
      const id = event.target.id;
      setValues((fields) => ({
        ...fields,
        [id]: value,
      }));
    },
  ];
}

export function useBreadcrumb() {
  const [breadcrumbs, setBc] = useState([]);
  const history = useHistory();
  const setBreadcrumbs = (routes, match) => {
    const crumbs = routes
      .filter(({ path, force }) => match.path.includes(path) || force)
      .map(({ path, hash, ...rest }) => ({
        path: Object.keys(match.params).length
          ? Object.keys(match.params).reduce(
              (path, param) => path.replace(`:${param}`, match.params[param]),
              path
            )
          : path,
        action: () => history.push(hash ? `${path}${hash}` : path),
        ...rest,
      }));
    setBc(crumbs);
  };

  return [breadcrumbs, setBreadcrumbs];
}

// TODO: this one is duplicated in boss-ui consider using that one
export function useMarked(markdown, options = MD_DEFAULT_OPTIONS) {
  const [html, setHtml] = useState(markdown);

  useEffect(() => {
    if (options.markedOptions) {
      marked.setOptions(options.markedOptions);
    }
    const tokens = marked.lexer(markdown);
    const html = marked.parser(tokens);
    setHtml(options.skipSanitize ? html : sanitizeHTML(html, options.sanitizeOptions));
  }, [markdown, options]);

  return html;
}

export function useRefreshScore(loading = false) {
  const [refreshHover, setRefreshHover] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(0);
  const resetLastUpdate = () => setLastUpdate(0);

  const getLastUpdatedLabel = () => {
    if (lastUpdate < 1) {
      return 'seconds ago';
    }
    if (lastUpdate === 1) {
      return 'a minute ago';
    }
    return `${lastUpdate} minutes ago`;
  };

  useEffect(() => {
    setLastUpdate(0);
    const update = setInterval(() => {
      setLastUpdate((p) => p + 1);
    }, 60000);
    return () => clearInterval(update);
  }, [loading]);

  return [refreshHover, setRefreshHover, getLastUpdatedLabel, resetLastUpdate];
}

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function useLocalStorage(key, initialValue) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };
  return [storedValue, setValue];
}
